import { yupResolver } from '@hookform/resolvers/yup'
import { css, cx } from '@linaria/core'
import {
  BodyText,
  Button,
  ButtonGroup,
  elFlex1,
  elHFull,
  elMt8,
  FlexContainer,
  FormLayout,
  InputError,
  InputGroup,
  InputWrapFull,
  Label,
  Select,
} from '@reapit/elements'
import { DialogWrapper } from '@reapitform/common-ui'
import { sendForActionSchema, TSendForAction, useSendForAction } from '../../api/documents/actions/send-for-signing'
import React from 'react'
import { useForm } from 'react-hook-form'

const deadlineOptions = [
  { title: '24 hours', value: 24 },
  { title: '48 hours', value: 48 },
  { title: '72 hours', value: 72 },
  { title: '7 days', value: 168 },
  { title: '30 days', value: 720 },
  { title: '90 days', value: 2160 },
]

interface IChangeExpiryProps {
  isOpen?: boolean
  onClose?: () => void
  onFinish?: (agreementIds: string[]) => void
  agreementId?: string[]
}

export const ChangeExpiryModal: React.FC<IChangeExpiryProps> = ({ isOpen = false, onClose, onFinish, agreementId }) => {
  const { isLoading, sendForSigning } = useSendForAction()

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm<TSendForAction>({
    defaultValues: {
      linkExpiryHours: '',
      iDs: undefined,
    },
    resolver: yupResolver(sendForActionSchema),
  })

  React.useEffect(() => {
    if (!agreementId) return
    setValue('iDs', agreementId)
  }, [agreementId])

  const confirmationMessage: string = React.useMemo(() => {
    if (!agreementId?.length) return ''
    const agreementLength: number = agreementId.length
    const isHaveMoreThan1: boolean = Boolean(agreementLength && agreementId.length > 1)

    if (isHaveMoreThan1)
      return `You have selected ${agreementLength} agreements. The link expiry will be updated and resent to those who haven't signed.`
    return "The link expiry will be updated and resent to those who haven't signed."
  }, [agreementId?.length])

  if (!agreementId || !agreementId.length) return

  const onSubmit = async (data: TSendForAction) => {
    await sendForSigning(data)
    onFinish?.(agreementId)
    onClose?.()
    reset()
  }

  const handleClose = () => {
    onClose?.()
    reset()
  }

  return (
    <DialogWrapper handlerModalClose={handleClose} isModalOpen={isOpen} title="Change Expiry">
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexContainer isFlexColumn className={cx(elHFull, elFlex1)}>
          <BodyText hasSectionMargin>{confirmationMessage}</BodyText>
          <FormLayout>
            <InputWrapFull>
              <InputGroup>
                <Label>Deadline *</Label>
                <Select
                  className={cx(
                    errors.linkExpiryHours?.message &&
                      css`
                        background-color: var(--color-red-100);
                        border: 1px solid var(--intent-danger);
                      `,
                  )}
                  {...register('linkExpiryHours')}
                >
                  <option value="">Select</option>
                  {deadlineOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.title}
                    </option>
                  ))}
                </Select>
                {errors.linkExpiryHours?.message && <InputError message="Deadline is required" />}
              </InputGroup>
            </InputWrapFull>
          </FormLayout>
          <ButtonGroup alignment="right" className={cx(elMt8)}>
            <Button intent="default" disabled={isLoading} onClick={handleClose}>
              Cancel
            </Button>
            <Button loading={isLoading} disabled={isLoading} intent="primary" type="submit">
              Change and Resend
            </Button>
          </ButtonGroup>
        </FlexContainer>
      </form>
    </DialogWrapper>
  )
}
